import React, { PureComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import { FaFileSignature } from 'react-icons/fa';
import SignalRService from '@/shared/services/SignalRService';
import config from '@/config';
import api from '@/shared/api';
import authService from '@/shared/services/AuthService';
import { CanCreate, CanRead, CanUpdate, CanDelete } from '@/shared/components/security';
import FileIcon from '@/shared/components/FileIcon';
import FileUpload from '@/shared/components/input/FileUpload';
import { FolderOpenIcon, MailIcon } from '@/shared/components/Icons';
import DocumentSelectorModal from '@/shared/components/DocumentSelectorModal';
import ShareItemModal from '@/shared/components/ShareItemModal';
import FormattedDateShort from '@/shared/components/FormattedDateShort';
import TaskDetailsSentEmails from './TaskDetailsSentEmails';
import Button from '@/shared/components/Button';
import DocumentDetailsModal from '@/modules/documents/details/DocumentDetailsModal';
import DocumentService from '@/shared/services/DocumentService';
import YesNoModal from '@/shared/components/YesNoModal';

import messages from '../taskMessages';
import ToastrService from '@/shared/services/ToastrService';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import EditableDocumentTypeSelect from '@/modules/documents/EditableDocumentTypeSelect';
import handleHttpError from '@/shared/services/HandleHttpError';
import DataCacheService from '@/shared/services/DataCacheService';

const getSigningStatusColor = signingStatus => {
  switch (signingStatus) {
    case 1:
      return '';
    case 2:
      return 'text-orange';
    case 3:
    case 4:
    case 11:
    case 12:
      return 'text-danger';
    case 10:
      return 'text-blue';
    case 20:
    case 21:
      return 'text-success';
    default:
      return '';
  }
};
class TaskDetailsDocuments extends PureComponent {
  state = {
    attachments: [],
    emails: [],
    savingBeforeEmail: false,
    deleteAttachment: null,
  };

  documentUpdatedSignalRHandler = document => {
    setTimeout(() => this.onDocumentUpdated(document, false), 2000);
  };
  componentDidMount() {
    this.retrieveDocumentAndMails();
    SignalRService.subscribe('documentUpdated', this.documentUpdatedSignalRHandler);
  }
  componentWillUnmount() {
    SignalRService.unsubscribe('documentUpdated', this.documentUpdatedSignalRHandler);
  }
  componentDidUpdate(prevProps) {
    const { taskIds, taskId } = this.props;
    if (
      taskId !== prevProps.taskId ||
      JSON.stringify(prevProps.taskIds) !== JSON.stringify(taskIds)
    ) {
      this.retrieveDocumentAndMails();
    }
  }

  retrieveDocumentAndMails = () => {
    const { intl, taskIds } = this.props;
    // retrieve both linked documents & sent emails for the task(s)
    if (!authService.hasPermission({ functionality: 'Documents', privilege: 'Read' })) return;
    if (!taskIds || taskIds.length === 0) return;
    this.setState({ attachments: null, emails: null });

    const linkedDocumentsPromise = api.base.post(
      `${config.appUrls.documents}/api/documents/DocumentLinks`,
      taskIds,
    );
    const sentEmailsPromise = api.base.post(
      `${config.appUrls.documents}/api/emails/TaskHistory`,
      taskIds,
    );

    Promise.all([linkedDocumentsPromise, sentEmailsPromise]).then(
      ([{ data: attachments = [] }, { data: emails = [] }]) => {
        this.setState({
          attachments,
          emails,
        });
      },
      error => {
        handleHttpError(intl)(error);
      },
    );
  };

  openMailModal = () => {
    const { onOpenMailModal } = this.props;
    this.setState({ savingBeforeEmail: true });
    Promise.resolve(onOpenMailModal()).finally(() => {
      this.setState({ savingBeforeEmail: false });
    });
  };

  deleteAttachment = attachment => {
    const { taskIds, taskId } = this.props;
    // if this modal is opened on the main task, the attachments could also include documents that are linked to one of the sub tasks
    // when clicking the delete attachment button, we have to find the correct taskId, in order to delete the link

    let linkId = null;
    if (attachment.links) {
      const tasksLinkedToAttachment = attachment.links
        .filter(x => x.linkType === 5)
        .map(x => x.linkId);
      linkId = taskIds.filter(x => tasksLinkedToAttachment.includes(x))[0];
    } else {
      linkId = taskId;
    }

    api.base
      .delete(`${config.appUrls.documents}/api/documents/links/${attachment.id}/${linkId}/5`)
      .then(
        () => {
          this.setState(state => ({
            deleteAttachment: null,
            attachments: state.attachments.filter(x => x.id !== attachment.id),
          }));
        },
        error => {
          const { intl } = this.props;
          handleHttpError(intl)(error);
        },
      );
  };

  onDocumentsSelected = documents => {
    const { taskId, relationId } = this.props;
    const { attachments } = this.state;
    const newDocuments = documents.filter(
      x => !(attachments || []).map(y => y.id).some(y => y === x.id),
    );
    if (newDocuments && newDocuments.length > 0) {
      let documentLinks = [];

      for (let i = 0; i < newDocuments.length; i++) {
        const document = newDocuments[i];

        // link document to task
        documentLinks = [
          ...documentLinks,
          {
            documentId: document.id,
            linkId: taskId,
            linkType: 5,
          },
        ];

        // link document to relation, if present
        if (relationId) {
          documentLinks = [
            ...documentLinks,
            {
              documentId: document.id,
              linkId: relationId,
              linkType: 3,
            },
          ];
        }
      }
      api.base.post(`${config.appUrls.documents}/api/documents/linksmultiple`, documentLinks).then(
        () => {
          this.setState(state => ({
            attachments: [...state.attachments, ...newDocuments],
          }));
        },
        error => {
          const { intl } = this.props;
          handleHttpError(intl)(error);
        },
      );
    }
  };
  onDocumentUpdated = (document, closeDialog = true) => {
    this.setState(state => {
      let hasChanged = false;
      const newAttachments = state.attachments.map(x => {
        if (x.id !== document.id) return x;
        hasChanged = true;
        return { ...x, ...document };
      });
      let newState = {};
      if (hasChanged) {
        newState.attachments = newAttachments;
      }
      if (closeDialog) {
        newState.documentDetailId = null;
      }

      return newState;
    });
  };

  getPreviousDocumentId = documentId => {
    const { attachments } = this.state;
    if (!attachments || !attachments.length) return;

    const currentIndex = attachments.findIndex(x => x.id === documentId);

    // No previous doc present
    if (currentIndex <= 0) return;

    const previousIndex = currentIndex - 1;
    if (previousIndex < attachments.length) {
      return attachments[previousIndex].id;
    }
  };
  getNextDocumentId = documentId => {
    const { attachments } = this.state;
    if (!attachments || !attachments.length) return;
    const currentIndex = attachments.findIndex(x => x.id === documentId);
    if (currentIndex < 0) return;

    const nextIndex = currentIndex + 1;

    // No next doc present
    if (nextIndex >= attachments.length) return;

    return attachments[nextIndex].id;
  };

  onDocumentUploaded = (error, document) => {
    const { intl } = this.props;

    if (error) {
      ToastrService.error(intl.formatMessage(messages.MsgUploadAttachmentFailed));
      return;
    }

    this.setState(({ attachments }) => ({
      attachments: [...attachments, document],
    }));
  };

  updateDocumentType = (document, documentType) => {
    const { intl } = this.props;

    return api.base
      .put(`${config.appUrls.documents}/api/documents/type`, { id: document.id, documentType })
      .then(() => {
        this.setState(state => ({
          attachments: state.attachments.map(x =>
            x.id !== document.id ? x : { ...x, documentType },
          ),
        }));
        ToastrService.success();
      })
      .catch(handleHttpError(intl));
  };
  render() {
    const { taskId, relationId, intl, disabled, showSendEmailButton } = this.props;
    const {
      attachments,
      emails,
      deleteAttachment,
      documentDetailId,
      savingBeforeEmail,
      showDocumentSelectorModal,
    } = this.state;
    const documentUploadEndpoint = `${
      config.appUrls.documents
    }/api/documents?documentType=-1&documentLinkType=5&linkId=${taskId}&source=301&relationId=${relationId ||
      ''}`;
    const canRead = authService.hasPermission({ functionality: 'Documents', privilege: 'Read' });

    return (
      <div className="task-detail-section documents">
        <h5 className="visible-lg-inline-block">{intl.formatMessage(messages.Documents)}</h5>
        {attachments ? (
          <table>
            <tbody>
              {attachments
                .sort((a, b) => new Date(b.documentDate) - new Date(a.documentDate))
                .map(attachment => (
                  <tr key={attachment.id}>
                    <td>
                      <FormattedDateShort value={attachment.documentDate} />
                    </td>
                    <td className="overflow-ellipsis">
                      {canRead ? (
                        <a
                          onClick={() => this.setState({ documentDetailId: attachment.id })}
                          title={intl.formatMessage(messages.OpenDocument)}
                        >
                          <FileIcon document={attachment} />
                          {attachment.name}
                        </a>
                      ) : (
                        <span>
                          <FileIcon document={attachment} />
                          {attachment.name}
                        </span>
                      )}
                    </td>
                    <td className="hidden-xs hidden-sm document-type">
                      <EditableDocumentTypeSelect
                        value={attachment.documentType}
                        onChange={type => this.updateDocumentType(attachment, type)}
                        required
                        disabled={
                          disabled ||
                          attachment.recognitionStatus === 1 ||
                          attachment.recognitionStatus === 3 ||
                          !attachment.canRemove
                        }
                      />
                    </td>
                    <td className="extra-icon-column">
                      <FaFileSignature
                        title={intl.formatMessage(
                          listItemMsg('signingStatus', attachment.signingStatus),
                        )}
                        size="1.2em"
                        className={getSigningStatusColor(attachment.signingStatus)}
                      />
                    </td>
                    <td className="actions-column hidden-xs text-right">
                      <div className="btn-group">
                        {canRead ? (
                          <i
                            onClick={() => DocumentService.download(attachment.id)}
                            title={intl.formatMessage(messages.DownloadAttachment)}
                            className="fa fa-download clickable margin5L"
                          />
                        ) : null}
                        <CanUpdate on="Documents">
                          {attachment.canEdit && !DataCacheService.hasUserConnectPlatform(46) && (
                            <i
                              onClick={() =>
                                DocumentService.edit(attachment.id, intl.locale.split('-')[0])
                              }
                              title={intl.formatMessage(messages.EditDocument)}
                              className="fa fa-pencil clickable margin10L"
                            />
                          )}
                        </CanUpdate>
                        <CanUpdate on="Documents">
                          <i
                            onClick={() => this.setState({ deleteAttachment: attachment })}
                            title={intl.formatMessage(messages.DeleteAttachment)}
                            className="fa fa-trash-o clickable margin10L"
                          />
                        </CanUpdate>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        ) : (
          <LoadingAnimation />
        )}

        <CanCreate on="documents">
          {!disabled ? (
            <div className="file-upload-wrapper margin10B">
              <FileUpload
                endpoint={documentUploadEndpoint}
                onFileUploaded={(error, file) => this.onDocumentUploaded(error, file)}
                removeSuccessfullAfter={500}
              />
            </div>
          ) : null}
          <button
            type="button"
            disabled={disabled}
            className="width100pct btn btn-default btn-grey btn-sm"
            onClick={() => this.setState({ showDocumentSelectorModal: true })}
          >
            <FolderOpenIcon className="margin5R" />
            <FormattedMessage {...messages.ChooseDocumentInAdminPulse} />
          </button>
        </CanCreate>

        {showDocumentSelectorModal && (
          <DocumentSelectorModal
            show
            initialRelation={relationId}
            initialLinkTypes={this.props.initialLinkTypes}
            initialSelectedDocuments={attachments}
            onDocumentsSelected={documents => {
              this.setState({ showDocumentSelectorModal: false }, () =>
                this.onDocumentsSelected(documents),
              );
            }}
            onClose={() => this.setState({ showDocumentSelectorModal: false })}
            disabled={disabled}
          />
        )}

        {emails && emails.length > 0 && <TaskDetailsSentEmails emails={emails} />}

        {showSendEmailButton ? (
          <CanCreate on="InteractionsOwn">
            <Button
              onClick={this.openMailModal}
              btnClassName="btn-inverse btn-xs margin20T"
              disabled={savingBeforeEmail}
            >
              {savingBeforeEmail && <i className="fa fa-spinner fa-spin margin5R" />}
              {!savingBeforeEmail && <MailIcon color="white" className="margin5R" />}

              {intl.formatMessage(messages.SendEmail)}
            </Button>
          </CanCreate>
        ) : null}

        {documentDetailId ? (
          <DocumentDetailsModal
            documentId={documentDetailId}
            show
            getPreviousDocumentId={this.getPreviousDocumentId}
            getNextDocumentId={this.getNextDocumentId}
            onSave={this.onDocumentUpdated}
            onClose={() => this.setState({ documentDetailId: null })}
            disabled={disabled}
          />
        ) : null}

        {deleteAttachment ? (
          <YesNoModal
            show={!!deleteAttachment}
            disabled={disabled}
            title={intl.formatMessage(messages.UnlinkDocument)}
            message={intl.formatMessage(messages.UnlinkDocumentConfirmationMsg)}
            onNo={() =>
              this.setState({
                deleteAttachment: null,
              })
            }
            onSave={() => this.deleteAttachment(deleteAttachment)}
          />
        ) : null}
      </div>
    );
  }
}
export default TaskDetailsDocuments;
